import LangEnum from '../../../../lang/LangEnum';
import { CommonParamsWithOfflineOrder } from '../../../orders/order.types';
import { AvailabilityEnum } from '../../../products/ProductTypes';
import { OfflineDatabaseCategory } from '../../database/offlineDatabase/types/offlineDatabaseCategory.types';
import { OfflineDatabaseClassification } from '../../database/offlineDatabase/types/offlineDatabaseClassification.types';
import { OfflineDatabaseCollection } from '../../database/offlineDatabase/types/offlineDatabaseCollection.types';
import { OfflineDatabaseCustomer } from '../../database/offlineDatabase/types/offlineDatabaseCustomer.types';
import { OfflineDatabaseDeliveryWindow } from '../../database/offlineDatabase/types/offlineDatabaseDeliveryWindow.types';
import { OfflineDatabaseKit } from '../../database/offlineDatabase/types/offlineDatabaseKit.types';
import { OfflineDatabasePriceTable } from '../../database/offlineDatabase/types/offlineDatabasePriceTable.types';
import { OfflineDatabaseProduct } from '../../database/offlineDatabase/types/offlineDatabaseProduct.types';
import { OfflineDatabaseProductColor } from '../../database/offlineDatabase/types/offlineDatabaseProductColor.types';
import { OfflineDatabaseProductColorPriceInfo } from '../../database/offlineDatabase/types/offlineDatabaseProductColorPriceInfo.types';
import { OfflineDatabaseSalesRepresentative } from '../../database/offlineDatabase/types/offlineDatabaseSalesRepresentative.types';
import { OfflineDatabaseSeller } from '../../database/offlineDatabase/types/offlineDatabaseSeller.types';
import { OfflineOrderProduct } from './orderProduct/OfflineOrderProduct';
import {
  OfflineDatabasePackWithMultiplier,
  OfflineGridItem,
} from './orderProduct/offlineOrderProduct.types';

export interface OfflineOrderCreateParams {
  id: string;
  brandId: string;
  createdAt: Date;
  updatedAt: Date;
  status: OfflineStatus;
  priceTable: OfflineDatabasePriceTable;
  code?: number;
  observation?: string;
  seller?: OfflineDatabaseSeller;
  products?: OfflineOrderProduct[];
  customer?: OfflineDatabaseCustomer;
  salesRepresentative?: OfflineDatabaseSalesRepresentative;
  metadata?: OfflineOrderMetadata;
}

export enum OfflineStatus {
  OFFLINE = 'OFFLINE',
  SYNCING = 'SYNCING',
  ERROR_SYNCING = 'ERROR_SYNCING',
}

export const isOfflineStatus = (status?: string): status is OfflineStatus =>
  Object.values(OfflineStatus).includes(status as OfflineStatus);

export interface OfflineOrderStaticCreateParams {
  seller: OfflineDatabaseSeller;
  priceTable: OfflineDatabasePriceTable;
  customer?: OfflineDatabaseCustomer;
  salesRepresentative?: OfflineDatabaseSalesRepresentative;
}

export interface AddProductParams {
  grid: Array<Omit<OfflineGridItem, 'id'> & { id?: string }>;
  product: OfflineDatabaseProduct;
  productColor: OfflineDatabaseProductColor;
  availability: AvailabilityEnum;
  productColorPriceInfo: OfflineDatabaseProductColorPriceInfo;
  kit?: OfflineDatabaseKit;
  productCategory?: OfflineDatabaseCategory;
  productCollection?: OfflineDatabaseCollection;
  deliveryWindow?: OfflineDatabaseDeliveryWindow;
  packsWithMultiplier?: OfflineDatabasePackWithMultiplier[];
  productColorClassifications?: OfflineDatabaseClassification[];
}

export interface OrderProductOnlyIds {
  productColorId: string;
  availability: AvailabilityEnum;
  deliveryWindowId?: string;
}

export interface ChangeProductGridParams {
  orderProductIds: OrderProductOnlyIds;
  gridValues: Array<{
    id: string;
    quantity: number;
  }>;
}

export interface ChangeProductGridBasedOnPacksParams {
  orderProductIds: OrderProductOnlyIds;
  productColorWithAllPacks: OfflineDatabaseProductColor;
  packsWithMultiplier: OfflineDatabasePackWithMultiplier[];
}

export interface GetOrderProductParams extends OrderProductOnlyIds {}

export interface SetPriceTableAdditionalData {
  productsPriceInfo: OfflineDatabaseProductColorPriceInfo[];
}

export interface OfflineOrderOnlineMetadata {
  onlineOrderId: string;
  onlineOrderCode: string;
}

interface ErrorMetadata {
  lastSyncingError: Date;
  errorMessage?: LangEnum;
}

export interface OfflineOrderMetadata {
  errorMetadata?: ErrorMetadata;
  onlineMetadata?: OfflineOrderOnlineMetadata;
}

export type CommonParamsWithOnlineOrder = Omit<
  CommonParamsWithOfflineOrder,
  'customer' | 'priceTable' | 'salesRepresentative' | 'seller'
>;
